/**
 * engines.ts -- smile models. Port of `models/sabr_formulas.py`,
 * `models/rough_sabr.py` and `backtest/engines.py`.
 *
 * An engine's only job is `impliedVol(bucket, K) -> sigma`. Price, Greeks, term
 * interpolation and execution are shared in surface.ts, which is what makes
 * "the same fills under two models" a fair comparison: the runs differ only in
 * the vol read off the smile.
 *
 * The smile is evaluated at `bucket.tQuote` -- the T it was calibrated at -- not
 * at `bucket.T`. Using T here silently re-prices the smile away from the quoted
 * vols.
 */

import { PricingFailure, type SmileBucket } from "./core";

const ATM_EPS = 1e-12; // identical to sabr_formulas._ATM_EPS
const EPS = 1e-12; // identical to rough_sabr._EPS

// ===========================================================================
// Hagan-West shifted SABR, normal vol
// ===========================================================================

function alphaBar(
  alpha: number,
  beta: number,
  rho: number,
  nu: number,
  Fbar: number,
  T: number,
): number {
  return alpha * (1.0 + 0.25 * alpha * beta * rho * nu * Math.pow(Fbar, beta - 1.0) * T);
}

function delta0(beta: number, Fbar: number): number {
  return (-beta * (2.0 - beta)) / (8.0 * Math.pow(Fbar, 2.0 - 2.0 * beta));
}

function zFunc(
  aBar: number,
  beta: number,
  nu: number,
  Fbar: number,
  Kbar: number,
): number {
  const nuOverA = nu / aBar;
  if (Math.abs(beta - 1.0) < 1e-12) return nuOverA * Math.log(Kbar / Fbar);
  const oneMB = 1.0 - beta;
  return (nuOverA * (Math.pow(Kbar, oneMB) - Math.pow(Fbar, oneMB))) / oneMB;
}

function eFunc(z: number, rho: number): number {
  return Math.sqrt(1.0 + 2.0 * rho * z + z * z);
}

function yFunc(z: number, rho: number, E: number): number {
  return Math.log((z + rho + E) / (1.0 + rho));
}

function thetaFunc(
  aBar: number,
  beta: number,
  rho: number,
  nu: number,
  Fbar: number,
  z: number,
  E: number,
  Y: number,
): number {
  const d0 = delta0(beta, Fbar);
  const a2 = aBar * aBar;
  const nu2 = nu * nu;
  const YE = Y * E;
  // Same 1e-14 degenerate guards and the same fallback constants as the Python
  // (`sabr_formulas._theta`). Note those fallbacks are not the true z->0 limits
  // -- see METHODOLOGY.md -- but they are unreachable because the ATM_EPS
  // branch fires first, and the port must match the Python bug-for-bug or the
  // parity test is meaningless.
  const term1 = Math.abs(YE) < 1e-14 ? 1.0 : (z + rho - rho * E) / YE;
  const bracket1 = -1.0 + 3.0 * term1;
  const bracket2 =
    Math.abs(Y) < 1e-14
      ? 1.0 - rho * rho
      : 1.0 - rho * rho + ((z + rho) * E - rho) / Y;
  return (nu2 / 24.0) * bracket1 + ((a2 * d0) / 6.0) * bracket2;
}

function zFactor(theta: number, T: number): number {
  return theta >= 0.0 ? 1.0 + theta * T : 1.0 / (1.0 - theta * T);
}

/** Shifted-SABR normal implied vol, Hagan-West (2017) eqs (27)-(32). */
export function haganNormalVol(
  F: number,
  K: number,
  T: number,
  alpha: number,
  beta: number,
  rho: number,
  nu: number,
  lam = 0.0,
): number {
  const Fbar = F + lam;
  const Kbar = K + lam;
  const aBar = alphaBar(alpha, beta, rho, nu, Fbar, T);

  if (Math.abs(Fbar - Kbar) < ATM_EPS) {
    const d0 = delta0(beta, Fbar);
    const Zatm =
      1.0 +
      (((nu * nu) / 24.0) * (2.0 - 3.0 * rho * rho) + (aBar * aBar * d0) / 3.0) * T;
    return aBar * Math.pow(Fbar, beta) * Zatm;
  }

  const z = zFunc(aBar, beta, nu, Fbar, Kbar);
  const E = eFunc(z, rho);
  const Y = yFunc(z, rho, E);
  const th = thetaFunc(aBar, beta, rho, nu, Fbar, z, E, Y);
  const Z = zFactor(th, T);
  const safeY = Math.abs(Y) < 1e-14 ? 1.0 : Y;
  return (nu * (Kbar - Fbar) * Z) / safeY;
}

// ===========================================================================
// Rough SABR, Fukasawa-Gatheral
// ===========================================================================

function gHalf(y: number, rho: number): number {
  const inner = Math.sqrt(1.0 + rho * y + 0.25 * y * y) - rho - 0.5 * y;
  const denom = 1.0 - rho + EPS;
  // Floor at EPS, matching `rough_sabr.G_half`. The previous JS port floored at
  // 1e-300 instead, which in the clamped regime gives 4·ln(1e-300)² ≈ 1.91e6
  // against Python's 4·ln(1e-12)² ≈ 3.05e3 -- a 625x divergence in G_A, so a
  // completely different wing vol.
  const arg = Math.max(inner / denom, EPS);
  const l = Math.log(arg);
  return 4.0 * l * l;
}

function gZero(y: number, rho: number): number {
  const quad = Math.max(1.0 + 2.0 * rho * y + y * y, EPS);
  const s = Math.sqrt(Math.max(1.0 - rho * rho, EPS));
  return Math.log(quad) + ((2.0 * rho) / s) * (Math.atan(rho / s) - Math.atan((y + rho) / s));
}

function gA(y: number, H: number, rho: number): number {
  const p1 = 2.0 * H + 1.0;
  const p3 = 2.0 * H + 3.0;
  const a = (3.0 * (1.0 - 2.0 * H)) / p3;
  const b = (2.0 * H) / p3;
  return p1 * p1 * (a * gZero(y / p1, rho) + b * gHalf((2.0 * y) / p1, rho));
}

function fFunction(y: number, H: number, rho: number): number {
  if (Math.abs(y) < 1e-4) return 1.0; // same cutoff as rough_sabr.f_function
  return Math.abs(y) / Math.sqrt(Math.max(gA(y, H, rho), EPS));
}

export function kappa(T: number, H: number, eta: number): number {
  return eta * Math.sqrt(Math.max(2.0 * H, EPS)) * Math.pow(Math.max(T, EPS), H - 0.5);
}

/** Rough-SABR Bachelier implied vol. ATM is exact because f(0) = 1. */
export function roughSabrNormalVol(
  F: number,
  K: number,
  T: number,
  atmVol: number,
  H: number,
  rho: number,
  eta: number,
): number {
  const U = atmVol;
  const y = (kappa(T, H, eta) * (K - F)) / Math.max(U, EPS);
  return U * fFunction(y, H, rho);
}

// ===========================================================================
// Engines
// ===========================================================================

export interface PricingEngine {
  readonly name: string;
  impliedVol(bucket: SmileBucket, K: number): number;
}

export class SABREngine implements PricingEngine {
  readonly name = "SABR";

  impliedVol(bucket: SmileBucket, K: number): number {
    const p = bucket.sabr;
    if (!p) {
      throw new PricingFailure(
        `${bucket.expiry}x${bucket.tenor} has no SABR parameters`,
      );
    }
    // The Hagan-West expansion needs a positive shifted strike; below -lam it
    // returns a silent NaN, which the previous engines propagated into price,
    // delta and gamma. EUR grids quote +/-300bp against forwards that were
    // often negative, so this is reachable, not theoretical.
    if (K + p.lam <= 0.0 || bucket.F + p.lam <= 0.0) {
      throw new PricingFailure(
        `shifted strike/forward non-positive: K=${K.toFixed(6)}, ` +
          `F=${bucket.F.toFixed(6)}, lam=${p.lam} at ${bucket.expiry}x${bucket.tenor}`,
      );
    }
    const v = haganNormalVol(
      bucket.F,
      K,
      bucket.tQuote,
      p.alpha,
      p.beta,
      p.rho,
      p.nu,
      p.lam,
    );
    if (!Number.isFinite(v) || v <= 0.0) {
      throw new PricingFailure(
        `SABR returned sigma=${v} at ${bucket.expiry}x${bucket.tenor}, K=${K}`,
      );
    }
    return v;
  }
}

export class RoughSABREngine implements PricingEngine {
  readonly name = "rough-SABR";

  impliedVol(bucket: SmileBucket, K: number): number {
    const p = bucket.rough;
    if (!p) {
      throw new PricingFailure(
        `${bucket.expiry}x${bucket.tenor} has no rough-SABR parameters`,
      );
    }
    const v = roughSabrNormalVol(
      bucket.F,
      K,
      bucket.tQuote,
      p.atmVol,
      p.H,
      p.rho,
      p.eta,
    );
    if (!Number.isFinite(v) || v <= 0.0) {
      throw new PricingFailure(
        `rough-SABR returned sigma=${v} at ${bucket.expiry}x${bucket.tenor}, K=${K}`,
      );
    }
    return v;
  }
}

/** Constant vol. Used by tests to check accounting without smile dynamics. */
export class FlatVolEngine implements PricingEngine {
  readonly name = "flat";
  constructor(private readonly sigma = 0.005) {}
  impliedVol(): number {
    return this.sigma;
  }
}

export function buildEngine(name: string): PricingEngine {
  switch (name.toLowerCase().replace(/-/g, "_")) {
    case "sabr":
    case "hagan":
      return new SABREngine();
    case "rough":
    case "rsabr":
    case "rough_sabr":
      return new RoughSABREngine();
    case "flat":
      return new FlatVolEngine();
    default:
      throw new Error(`unknown engine: ${name}`);
  }
}
